import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { rowBetweenDateInputs } from "./orderFilterHelpers";
import StakingService from "../../../api/services/StakingService";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import StakeModal from "../Staking/StakeModal";
import RedeemModal from "../Staking/RedeemModal";
import CancelStakingModal from "../Staking/CancelStakingModal";
import { getTotalInvestedAmount, resolvePackageFromPosition, getPackageCurrencyId, resolveStakeWalletBalance } from "../Staking/stakingStakeHelpers";
import { findFundRowBySymbol } from "../WithdrawPage/withdrawPageHelpers";
import "../Staking/staking.css";

const ORDER_DESKTOP_COL_COUNT = 8;

const STAKING_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "LOCKED", label: "Locked" },
  { value: "FLEXIBLE", label: "Flexible" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function parseDec(val) {
  if (val == null) return "0";
  if (typeof val === "object" && val.$numberDecimal != null) return val.$numberDecimal;
  return String(val);
}

function formatAmount(val, decimals = 8) {
  const n = parseFloat(parseDec(val));
  if (isNaN(n)) return "0";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: decimals });
}

function resolveIcon(iconPath, currency) {
  if (!iconPath) {
    return `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
  }
  return resolvePublicUploadUrl(iconPath)
    || `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value}</span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const StatusBadge = ({ value }) => {
  const map = {
    ACTIVE: { cls: "so-stakeType--flexible", label: "Active" },
    COMPLETED: { cls: "so-stakeType--locked", label: "Completed" },
    CANCELLED: { cls: "so-badge--closed", label: "Cancelled" },
  };
  const cfg = map[value] || { cls: "so-badge--closed", label: value };
  return <span className={`so-stakeType ${cfg.cls}`}>{cfg.label}</span>;
};

const StakingDetailModal = ({ position, onClose }) => {
  const pkg = position.packageId || {};
  const icon = resolveIcon(pkg.iconPath, position.currency);
  const apr = pkg.returnPercentage ?? pkg.aprMin ?? "—";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const DetailRow = ({ label, value, highlight }) => (
    <div className="stake-overview-row">
      <span className="stake-overview-row__label">{label}</span>
      <span className={`stake-overview-row__value${highlight ? " stake-overview-row__value--green" : ""}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="stake-overview-overlay" onClick={onClose}>
      <div className="stake-overview-content stake-payout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stake-overview-header border-bottom">
          <div className="stake-overview-header__left">
            <div className="stake-overview-title">
              <img
                src={icon}
                alt={position.currency}
                className="mint-title-icon"
                onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(position.currency || "usdt").toLowerCase()}_icon.svg`; }}
              />
              <div>
                <span className="stake-detail-title-text">{position.currency}</span>
                <span className="stake-detail-subtitle-text">{position.currencyFullName}</span>
              </div>
            </div>
            <div className="stake-detail-desc">Staking Position Details</div>
          </div>
          <button type="button" className="mint-modal-close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="stake-detail-body">
          <div className="stake-detail-badge-row">
            <StatusBadge value={position.status} />
            <span className="stake-detail-type-text">{position.stakingType}</span>
          </div>

          <div className="stake-overview-body">
            <DetailRow label="Invested Amount" value={`${formatAmount(getTotalInvestedAmount(position))} ${position.currency}`} />
            <DetailRow label="Profit" value={`${formatAmount(position.totalRewardEarned)} ${position.currency}`} highlight />
          </div>

          <div className="stake-overview-body">
            <DetailRow label="APR" value={apr !== "—" ? `${apr}%` : "—"} highlight />
            <DetailRow label="Duration" value={`${position.durationDays ?? pkg.duration ?? "—"} Days`} />
            <DetailRow label="Wallet Type" value={position.walletType ?? "—"} />
            <DetailRow label="Credited To" value={position.creditedWalletType ?? "—"} />
          </div>

          <div className="stake-overview-body">
            <DetailRow label="Start Date" value={formatDate(position.startDate)} />
            <DetailRow label="End Date" value={formatDate(position.endDate)} />
            <DetailRow label="Next Payout Date" value={formatDate(position.nextPayoutDate)} />
            <DetailRow label="Created At" value={formatDate(position.createdAt)} />
          </div>

          {(position.penaltyPercent > 0 || parseFloat(parseDec(position.penaltyAmount)) > 0) && (
            <div className="stake-overview-body stake-overview-body--penalty">
              <DetailRow label="Penalty %" value={`${position.penaltyPercent ?? 0}%`} />
              <DetailRow label="Penalty Amount" value={`${formatAmount(position.penaltyAmount)} ${position.currency}`} />
              <DetailRow label="Refund Amount" value={`${formatAmount(position.refundAmount)} ${position.currency}`} />
            </div>
          )}

          {Array.isArray(position.payoutHistory) && position.payoutHistory.length > 0 && (
            <div className="stake-detail-history-wrap">
              <div className="stake-payout-modal__recordsTitle">Payout History</div>
              <div className="stake-payout-modal__list">
                {position.payoutHistory.map((ph, i) => (
                  <div key={i} className="stake-payout-modal__record stake-detail-history-row">
                    <span className="stake-payout-modal__recordBottom">{formatDate(ph.date || ph.paidAt)}</span>
                    <span className="stake-payout-modal__recordAmount">
                      +{formatAmount(ph.amount)} {position.currency}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ────────────────────────────────────────────── */
const StakingOrders = () => {
  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const fmtDate = (d) => d.toISOString().slice(0, 10);

  const [coin, setCoin] = useState("all");
  const [stakingType, setStakingType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(fmtDate(threeMonthsAgo));
  const [dateTo, setDateTo] = useState(fmtDate(today));
  const [openDropdown, setOpenDropdown] = useState(null);

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [stakePosition, setStakePosition] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemPosition, setRedeemPosition] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelPosition, setCancelPosition] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  /** symbol/currencyId -> Available balance, prefetched on load so the Staking (top-up) modal shows it instantly. */
  const balanceCacheRef = useRef(new Map());

  const prefetchStakeBalances = useCallback(async (rows) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || !Array.isArray(rows) || rows.length === 0) return;

    // One unique entry per currency: symbol -> currencyId (if known on the position's package).
    const bySymbol = new Map();
    rows.forEach((position) => {
      const pkg = resolvePackageFromPosition(position);
      const sym = String(pkg?.currency || position?.currency || "").toUpperCase();
      if (!sym) return;
      if (!bySymbol.has(sym)) bySymbol.set(sym, getPackageCurrencyId(pkg));
    });

    // Resolve any missing currencyIds from the earning wallet in a single call.
    if (Array.from(bySymbol.values()).some((id) => id == null)) {
      try {
        const walletRes = await StakingService.getEarningWallet();
        if (walletRes?.success && Array.isArray(walletRes.data)) {
          bySymbol.forEach((id, sym) => {
            if (id != null) return;
            const row = findFundRowBySymbol(walletRes.data, sym);
            const cid = row?.currency_id ?? row?.currencyId ?? null;
            if (cid != null) bySymbol.set(sym, cid);
          });
        }
      } catch {
        /* ignore — modal will fetch on open as a fallback */
      }
    }

    await Promise.all(
      Array.from(bySymbol.entries()).map(async ([sym, currencyId]) => {
        if (currencyId == null) return;
        try {
          const res = await StakingService.getUserBalance(currencyId, "earning");
          const bal = resolveStakeWalletBalance(res);
          balanceCacheRef.current.set(sym, bal);
          balanceCacheRef.current.set(String(currencyId), bal);
        } catch {
          /* ignore */
        }
      }),
    );
  }, []);

  const fetchPositions = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    const res = await StakingService.getMyPositions(page, 20);
    if (res?.success && Array.isArray(res.data)) {
      setPositions(res.data);
      if (res.pagination) setPagination((prev) => ({ ...prev, ...res.pagination, page }));
    } else {
      setError(res?.message || "Failed to load staking positions.");
      setPositions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPositions(1); }, [fetchPositions]);

  useEffect(() => {
    if (!loading && positions.length > 0) {
      prefetchStakeBalances(positions);
    }
  }, [loading, positions, prefetchStakeBalances]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const coinOptions = useMemo(() => {
    const coins = [...new Set(positions.map((p) => p.currency).filter(Boolean))];
    return [{ value: "all", label: "All" }, ...coins.map((c) => ({ value: c, label: c }))];
  }, [positions]);

  const filteredRows = useMemo(() => {
    return positions.filter((r) => {
      if (coin !== "all" && String(r.currency || "").toUpperCase() !== String(coin).toUpperCase()) return false;
      if (stakingType !== "all" && r.stakingType !== stakingType) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!rowBetweenDateInputs(r.startDate, dateFrom, dateTo)) return false;
      return true;
    });
  }, [positions, coin, stakingType, statusFilter, dateFrom, dateTo]);

  const resetFilters = () => {
    setCoin("all");
    setStakingType("all");
    setStatusFilter("all");
    setDateFrom(fmtDate(threeMonthsAgo));
    setDateTo(fmtDate(today));
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el?.click();
  };

  const isPackageActive = (position) => {
    if (String(position?.status || "").toUpperCase() !== "ACTIVE") return false;

    const pkg = position?.packageId;
    if (!pkg || typeof pkg !== "object") return true;

    const pkgStatus = pkg.status ?? pkg.packageStatus;
    if (pkgStatus == null || pkgStatus === "") return true;

    return String(pkgStatus).toUpperCase() === "ACTIVE";
  };

  const openStakeModal = (position) => {
    if (!isPackageActive(position)) return;
    setStakePosition(position);
    setShowStakeModal(true);
  };

  const openRedeemModal = (position) => {
    if (!position?._id || position.status !== "ACTIVE") return;
    setRedeemPosition(position);
    setShowRedeemModal(true);
  };

  const openCancelModal = (position) => {
    if (!position?._id || position.status !== "ACTIVE") return;
    setCancelPosition(position);
    setShowCancelModal(true);
  };

  const renderActionButtons = (position) => {
    const canAct = position.status === "ACTIVE";
    const canStake = isPackageActive(position);

    return (
      <div className="so-stkActionCell">
        <button
          type="button"
          className="so-stkActionBtn so-stkActionBtn--view"
          title="View details"
          onClick={() => setSelectedPosition(position)}
        >
          <i className="ri-eye-line" aria-hidden="true" />
          View
        </button>
        <button
          type="button"
          className="so-stkActionBtn so-stkActionBtn--stake"
          disabled={!canStake}
          onClick={() => openStakeModal(position)}
        >
          Staking
        </button>
        <button
          type="button"
          className="so-stkActionBtn so-stkActionBtn--redeem"
          disabled={!canAct}
          onClick={() => openRedeemModal(position)}
        >
          Redeem
        </button>
        <button
          type="button"
          className="so-stkActionBtn so-stkActionBtn--cancel"
          disabled={!canAct}
          onClick={() => openCancelModal(position)}
        >
          Cancel
        </button>
      </div>
    );
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter staking orders">
        <div className="so-toolbar__left">
          <CustomDropdown
            id={`${pref}stk-dd-coin`} label="Coin" value={coin} options={coinOptions}
            onChange={setCoin} menuKey={`${pref}stk-coin`} openKey={openDropdown} onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}stk-dd-type`} label="Type" value={stakingType} options={STAKING_TYPE_OPTIONS}
            onChange={setStakingType} menuKey={`${pref}stk-type`} openKey={openDropdown} onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}stk-dd-status`} label="Status" value={statusFilter} options={STATUS_OPTIONS}
            onChange={setStatusFilter} menuKey={`${pref}stk-status`} openKey={openDropdown} onOpenToggle={setOpenDropdown}
          />
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="so-toolbar__right">
          <Link to="/staking" className="so-goStakingBtn">Go to Staking</Link>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const renderLoadingBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} style={{ textAlign: "center", padding: "40px", color: "#848e9c" }}>
        Loading...
      </td>
    </tr>
  );

  const renderErrorBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} style={{ textAlign: "center", padding: "40px", color: "#f6465d" }}>
        {error}
      </td>
    </tr>
  );

  const renderRow = (r) => {
    const pkg = r.packageId || {};
    const icon = resolveIcon(pkg.iconPath, r.currency);
    const apr = pkg.returnPercentage ?? pkg.aprMin ?? "—";
    const aprStr = apr !== "—" ? `${apr}% APR` : "—";

    return (
      <tr key={r._id}>
        <td>
          <span className="so-stakingCoin">
            <img src={icon} alt={r.currency} width={24} height={24} className="so-stakingCoin__icon"
              onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(r.currency || "usdt").toLowerCase()}_icon.svg`; }} />
            <span className="so-num">{r.currency}</span>
          </span>
        </td>
        <td className="so-num">{formatDate(r.startDate)}</td>
        <td className="so-num">{formatDate(r.endDate)}</td>
        <td className="so-num">{formatAmount(getTotalInvestedAmount(r))} {r.currency}</td>
        <td className="so-fillQty--pos so-num">
          +{formatAmount(r.totalRewardEarned)} {r.currency}
        </td>
        <td><span>{aprStr}</span></td>
        <td><StatusBadge value={r.status} /></td>
        <td>{renderActionButtons(r)}</td>
      </tr>
    );
  };

  const renderMobileCard = (r) => {
    const pkg = r.packageId || {};
    const icon = resolveIcon(pkg.iconPath, r.currency);
    const apr = pkg.returnPercentage ?? pkg.aprMin ?? "—";
    const aprStr = apr !== "—" ? `${apr}% APR` : "—";

    return (
      <OrderMobileCardShell key={`m-${r._id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Staking details">
        <div className="so-mobileCard__row">
          <span>Coin</span>
          <span className="so-stakingCoin">
            <img src={icon} alt={r.currency} width={24} height={24} className="so-stakingCoin__icon"
              onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(r.currency || "usdt").toLowerCase()}_icon.svg`; }} />
            <span className="so-num">{r.currency}</span>
          </span>
        </div>
        <div className="so-mobileCard__row">
          <span>Start Date</span>
          <span className="so-num">{formatDate(r.startDate)}</span>
        </div>
        <div className="so-mobileCard__row">
          <span>End Date</span>
          <span className="so-num">{formatDate(r.endDate)}</span>
        </div>
        <div className="so-mobileCard__row">
          <span>Invested</span>
          <span className="so-num">{formatAmount(getTotalInvestedAmount(r))} {r.currency}</span>
        </div>
        <div className="so-mobileCard__row">
          <span>Cumulative Return</span>
          <span className="so-fillQty--pos so-num">+{formatAmount(r.totalRewardEarned)} {r.currency}</span>
        </div>
        <div className="so-mobileCard__row">
          <span>APR</span>
          <span className="so-stakeProtocol">{aprStr}</span>
        </div>
        <div className="so-mobileCard__row">
          <span>Status</span>
          <StatusBadge value={r.status} />
        </div>
        <div className="so-mobileCard__actions so-mobileCard__actions--split">
          <span className="so-mobileCard__statusLabel">Action</span>
          {renderActionButtons(r)}
        </div>
      </OrderMobileCardShell>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-staking-title">
            <h1 id="so-staking-title" className="so-page__title">Staking</h1>
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines so-table--staking">
                <thead>
                  <tr>
                    <th scope="col">Coin</th>
                    <th scope="col">Start Date</th>
                    <th scope="col">End Date</th>
                    <th scope="col">Invested</th>
                    <th scope="col">Reward Earned</th>
                    <th scope="col">APR</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? renderLoadingBody()
                    : error
                      ? renderErrorBody()
                      : filteredRows.length === 0
                        ? renderEmptyBody()
                        : filteredRows.map(renderRow)}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="staking_pagination" style={{ marginTop: "16px" }}>
                <button type="button" className="page_nav prev_nav"
                  disabled={pagination.page <= 1} onClick={() => fetchPositions(pagination.page - 1)}>‹</button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} type="button"
                    className={`page_num${pagination.page === p ? " active" : ""}`}
                    onClick={() => fetchPositions(p)}>{p}</button>
                ))}
                <button type="button" className="page_nav next_nav"
                  disabled={pagination.page >= pagination.totalPages} onClick={() => fetchPositions(pagination.page + 1)}>›</button>
              </div>
            )}
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Staking</h2>
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-staking-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#848e9c" }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#f6465d" }}>{error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map(renderMobileCard)
          )}
        </div>
      </div>

      {selectedPosition && (
        <StakingDetailModal position={selectedPosition} onClose={() => setSelectedPosition(null)} />
      )}

      {showStakeModal && stakePosition && (
        <StakeModal
          selectedPackage={resolvePackageFromPosition(stakePosition)}
          positionId={stakePosition._id}
          currentStakingAmount={getTotalInvestedAmount(stakePosition)}
          requireOverviewConfirm
          balanceCache={balanceCacheRef.current}
          onClose={() => {
            setShowStakeModal(false);
            setStakePosition(null);
          }}
          onSuccess={() => fetchPositions(pagination.page)}
        />
      )}

      {showRedeemModal && redeemPosition && (
        <RedeemModal
          position={redeemPosition}
          onClose={() => {
            setShowRedeemModal(false);
            setRedeemPosition(null);
          }}
          onSuccess={() => fetchPositions(pagination.page)}
        />
      )}

      {showCancelModal && cancelPosition && (
        <CancelStakingModal
          position={cancelPosition}
          onClose={() => {
            setShowCancelModal(false);
            setCancelPosition(null);
          }}
          onSuccess={() => fetchPositions(pagination.page)}
        />
      )}
    </div>
  );
};

export default StakingOrders;
